import React from "react";
import DataTable from "react-data-table-component";
import {Cell, Pie, PieChart, ResponsiveContainer, Tooltip} from "recharts";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faAddressCard,
    faBell,
    faBuilding,
    faCity,
    faCube,
    faEdit,
    faEnvelope,
    faFileAlt,
    faFlag,
    faGaugeHigh,
    faGlobe,
    faKey,
    faMailBulk,
    faMapMarkerAlt,
    faPhone,
    faPlus,
    faTimes,
    faUser,
    faUsers,
} from "@fortawesome/free-solid-svg-icons";
import CountrySelector from "../shared/CountrySelector";
import {
    COLORS,
    customStyles,
    dashboardStyles,
    getAssetColumns,
    getAssetTypeCounts,
    getAssetTypeData,
    getAssetTypeIcon,
    getClientColumns,
    getUserColumns,
} from "./utils";
import {PAGE_SIZE_OPTIONS} from "../../utils/paginationUtils";
import EmptyTableState from "../../utils/EmptyTableState";

// ==================== StatCard Component ====================
export function StatCard({ title, count, icon, gradient }) {
  return (
    <div style={dashboardStyles.compactCard}>
      <div style={{ ...dashboardStyles.iconBox, background: gradient }}>
        <FontAwesomeIcon icon={icon} />
      </div>
      <div style={dashboardStyles.cardContent}>
        <div style={dashboardStyles.cardTitle}>{title}</div>
        <div style={dashboardStyles.cardValue}>{count}</div>
      </div>
    </div>
  );
}

// ==================== DashboardView Component ====================
export function DashboardView({
  clients,
  clientsTotalRows,
  clientsPerPage,
  onClientsPageChange,
  onClientsPerRowsChange,
  globalAssets,
  onClientClick,
  onToggleClientStatus,
  onOpenEditModal,
  onOpenAddModal,
}) {
  const clientColumns = getClientColumns(onToggleClientStatus, onOpenEditModal);

  return (
    <>
      <div style={dashboardStyles.gridContainer}>
        <StatCard
          title="Total Clients"
          count={clientsTotalRows || clients.length}
          icon={faBuilding}
          gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        />
        <StatCard
          title="Active Users"
          count={clients.reduce((acc, c) => acc + (c.userCount || 0), 0) + 1}
          icon={faUsers}
          gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
        />

        <div style={dashboardStyles.chartArea}>
          <div style={dashboardStyles.donutCenter}>
            <div style={dashboardStyles.centerIcon}>
              <FontAwesomeIcon icon={faCube} />
            </div>
            <div style={dashboardStyles.centerValue}>{globalAssets.length}</div>
            <div style={dashboardStyles.centerLabel}>Total Assets</div>
          </div>

          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={getAssetTypeData(globalAssets)}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={65}
                outerRadius={80}
                paddingAngle={4}
                startAngle={90}
                endAngle={-270}
              >
                {getAssetTypeData(globalAssets).map((_, index) => (
                  <Cell
                    key={index}
                    fill={COLORS[index % COLORS.length]}
                    stroke="none"
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  borderRadius: "8px",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="main-table-card">
        <div className="table-header">
          <div>
            <h2 className="table-title">Client Directory</h2>
            <p className="table-subtitle">Manage all registered clients</p>
          </div>
          <button className="btn-primary" onClick={onOpenAddModal}>
            <FontAwesomeIcon icon={faPlus} />
            Add New Client
          </button>
        </div>
        <div className="table-wrapper">
          <DataTable
            columns={clientColumns}
            data={clients}
            pagination
            paginationServer
            paginationTotalRows={clientsTotalRows}
            paginationPerPage={clientsPerPage}
            paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onChangePage={onClientsPageChange}
            onChangeRowsPerPage={onClientsPerRowsChange}
            customStyles={customStyles}
            onRowClicked={onClientClick}
            highlightOnHover
            pointerOnHover
            noDataComponent={<EmptyTableState message="No clients registered yet" />}
          />
        </div>
      </div>
    </>
  );
}

// ==================== ClientDetailView Component ====================
export function ClientDetailView({
  selectedClient,
  clientAssets,
  clientUsers,
  onToggleFeature,
  onToggleAssetStatus,
  onUpdateOverspeedLimit,
}) {
  const assetColumns = getAssetColumns(onToggleAssetStatus);
  const userColumns = getUserColumns();

  // Local draft for the overspeed-limit input; seeded from the selected client and re-synced
  // whenever a different client is opened or the saved value changes.
  const [overspeedDraft, setOverspeedDraft] = React.useState(
    selectedClient?.overspeedLimitKmh ?? ""
  );
  React.useEffect(() => {
    setOverspeedDraft(selectedClient?.overspeedLimitKmh ?? "");
  }, [selectedClient?.clientId, selectedClient?.overspeedLimitKmh]);

  const savedOverspeed = selectedClient?.overspeedLimitKmh ?? "";
  const overspeedDirty = String(overspeedDraft) !== String(savedOverspeed);

  return (
    <div className="client-detail-layout">
      <div className="client-header-compact">
        <div className="client-info-compact">
          <div className="client-avatar-large">
            {selectedClient?.name?.charAt(0)}
          </div>
          <div className="client-basic-info">
            <h2 className="client-name-large">{selectedClient?.name}</h2>
            <p className="client-description">
              {selectedClient?.description || "No description available"}
            </p>
          </div>
        </div>

        <div className="client-quick-stats">
          <div className="quick-stats-group quick-stats-counts">
            <div className="quick-stat">
              <FontAwesomeIcon icon={faCube} className="quick-stat-icon" />
              <div>
                <div className="quick-stat-value">{clientAssets.length}</div>
                <div className="quick-stat-label">Assets</div>
              </div>
            </div>
            <div className="quick-stat">
              <FontAwesomeIcon icon={faUsers} className="quick-stat-icon" />
              <div>
                <div className="quick-stat-value">{clientUsers.length}</div>
                <div className="quick-stat-label">Members</div>
              </div>
            </div>
          </div>

          <div className="quick-stats-group quick-stats-features">
          <div className="quick-stat feature-toggle-stat">
            <FontAwesomeIcon icon={faBell} className="quick-stat-icon" />
            <div className="feature-toggle-content">
              <div className="quick-stat-label">Email Notifications</div>
              <div
                className="feature-toggle-switch"
                onClick={() =>
                  onToggleFeature(
                    "emailNotificationEnabled",
                    selectedClient?.emailNotificationEnabled
                  )
                }
                title="Toggle email notifications"
              >
                <div
                  className={`toggle-switch ${
                    selectedClient?.emailNotificationEnabled
                      ? "toggle-on"
                      : "toggle-off"
                  }`}
                >
                  <div className="toggle-knob" />
                </div>
                <span
                  className={`toggle-label ${
                    selectedClient?.emailNotificationEnabled
                      ? "label-active"
                      : "label-inactive"
                  }`}
                >
                  {selectedClient?.emailNotificationEnabled ? "ON" : "OFF"}
                </span>
              </div>
            </div>
          </div>

          <div className="quick-stat feature-toggle-stat">
            <FontAwesomeIcon icon={faKey} className="quick-stat-icon" />
            <div className="feature-toggle-content">
              <div className="quick-stat-label">Passcode Feature</div>
              <div
                className="feature-toggle-switch"
                onClick={() =>
                  onToggleFeature(
                    "passcodeFeature",
                    selectedClient?.passcodeFeature
                  )
                }
                title="Toggle passcode feature"
              >
                <div
                  className={`toggle-switch ${
                    selectedClient?.passcodeFeature ? "toggle-on" : "toggle-off"
                  }`}
                >
                  <div className="toggle-knob" />
                </div>
                <span
                  className={`toggle-label ${
                    selectedClient?.passcodeFeature
                      ? "label-active"
                      : "label-inactive"
                  }`}
                >
                  {selectedClient?.passcodeFeature ? "ON" : "OFF"}
                </span>
              </div>
            </div>
          </div>

          <div className="quick-stat feature-toggle-stat">
            <FontAwesomeIcon icon={faGaugeHigh} className="quick-stat-icon" />
            <div className="feature-toggle-content">
              <div className="quick-stat-label">Overspeed Limit (km/h)</div>
              <div className="overspeed-limit-control">
                <input
                  type="number"
                  min="1"
                  max="300"
                  className="overspeed-limit-input"
                  placeholder="Default (70)"
                  value={overspeedDraft}
                  onChange={(e) => setOverspeedDraft(e.target.value)}
                  title="Speed above which an overspeed alert fires for this fleet. Leave blank to use the system default."
                />
                <button
                  type="button"
                  className="overspeed-limit-save"
                  disabled={!overspeedDirty}
                  onClick={() => onUpdateOverspeedLimit(overspeedDraft)}
                  title="Save overspeed limit"
                >
                  Save
                </button>
              </div>
              <div className="overspeed-limit-current">
                {savedOverspeed !== "" && savedOverspeed != null
                  ? `Current: ${savedOverspeed} km/h`
                  : "Current: system default (70 km/h)"}
              </div>
            </div>
          </div>
          </div>
        </div>

        <div className="client-contact-info">
          <div className="contact-item">
            <FontAwesomeIcon icon={faEnvelope} />
            <span>{selectedClient?.emailId}</span>
          </div>
          <div className="contact-item">
            <FontAwesomeIcon icon={faPhone} />
            <span>{selectedClient?.phoneNumber || "Not provided"}</span>
          </div>
          <div className="contact-item">
            <FontAwesomeIcon icon={faBuilding} />
            <span>{selectedClient?.contactName}</span>
          </div>
        </div>
      </div>

      <div className="client-asset-stats-row">
        {Object.entries(getAssetTypeCounts(clientAssets)).map(
          ([type, count]) => (
            <div key={type} className="client-asset-stat-card">
              <div className="client-asset-stat-icon">
                {(() => {
                  const icon = getAssetTypeIcon(type);
                  return icon.kind === "image" ? (
                    <img
                      src={icon.value}
                      alt={type}
                      className={`asset-icon ${
                        icon.size === "large" ? "asset-icon-large" : ""
                      }`}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={icon.value}
                      className={`asset-icon-fa ${
                        icon.size === "large" ? "asset-icon-fa-large" : ""
                      }`}
                    />
                  );
                })()}
              </div>
              <div className="client-asset-stat-content">
                <div className="client-asset-stat-type">{type}</div>
                <div className="client-asset-stat-count">{count}</div>
              </div>
            </div>
          )
        )}
        {clientAssets.length === 0 && (
          <div className="client-asset-stat-card">
            <div
              className="client-asset-stat-icon"
              style={{
                background: "linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)",
              }}
            >
              <FontAwesomeIcon icon={faCube} />
            </div>
            <div className="client-asset-stat-content">
              <div className="client-asset-stat-type">No Assets</div>
              <div className="client-asset-stat-count">0</div>
            </div>
          </div>
        )}
      </div>

      <div className="client-tables-grid">
        <div className="data-table-card">
          <div className="table-header-simple">
            <div className="table-header-left">
              <h3>Assets</h3>
              <span className="count-badge">{clientAssets.length}</span>
            </div>
          </div>
          <div className="scrollable-table">
            <DataTable
              columns={assetColumns}
              data={clientAssets}
              pagination
              paginationPerPage={10}
              customStyles={customStyles}
              noDataComponent={<EmptyTableState message="No assets found" />}
            />
          </div>
        </div>

        <div className="data-table-card">
          <div className="table-header-simple">
            <div className="table-header-left">
              <h3>Team Members</h3>
              <span className="count-badge">{clientUsers.length}</span>
            </div>
          </div>
          <div className="scrollable-table">
            <DataTable
              columns={userColumns}
              data={clientUsers}
              pagination
              paginationPerPage={10}
              customStyles={customStyles}
              noDataComponent={<EmptyTableState message="No users found" />}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== ClientModal Component ====================
export function ClientModal({
  show,
  editMode,
  formData,
  onClose,
  onInputChange,
  onCountryChange,
  onSubmit,
}) {
  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-title">
            <FontAwesomeIcon
              icon={editMode ? faEdit : faBuilding}
              className="modal-title-icon"
            />
            <h3>{editMode ? "Edit Client" : "Register New Client"}</h3>
          </div>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={onSubmit}>
          <div className="form-section">
            <div className="form-section-header">
              <FontAwesomeIcon icon={faBuilding} className="section-icon" />
              <h4>Company Information</h4>
            </div>
            <div className="form-grid">
              <div className="form-group full-width">
                <label className="form-label">
                  <FontAwesomeIcon icon={faBuilding} className="label-icon" />
                  Company Name *
                </label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={onInputChange}
                  placeholder="Enter company name"
                  required
                />
              </div>
              <div className="form-group full-width">
                <label className="form-label">
                  <FontAwesomeIcon icon={faFileAlt} className="label-icon" />
                  Description
                </label>
                <input
                  type="text"
                  name="description"
                  className="form-input"
                  value={formData.description}
                  onChange={onInputChange}
                  placeholder="Brief description"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="section-icon" />
              <h4>Address Details</h4>
            </div>
            <div className="form-grid">
              <div className="form-group full-width">
                <label className="form-label">
                  <FontAwesomeIcon
                    icon={faMapMarkerAlt}
                    className="label-icon"
                  />
                  Street Address *
                </label>
                <input
                  type="text"
                  name="streetAddress"
                  className="form-input"
                  value={formData.streetAddress}
                  onChange={onInputChange}
                  placeholder="Street address"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faCity} className="label-icon" />
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  className="form-input"
                  value={formData.city}
                  onChange={onInputChange}
                  placeholder="City"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faFlag} className="label-icon" />
                  State *
                </label>
                <input
                  type="text"
                  name="state"
                  className="form-input"
                  value={formData.state}
                  onChange={onInputChange}
                  placeholder="State"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faMailBulk} className="label-icon" />
                  Postal Code *
                </label>
                <input
                  type="text"
                  name="postalCode"
                  className="form-input"
                  value={formData.postalCode}
                  onChange={onInputChange}
                  placeholder="Postal code"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faGlobe} className="label-icon" />
                  Country *
                </label>
                <CountrySelector
                  value={formData.isoCountryCode}
                  onChange={onCountryChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <FontAwesomeIcon icon={faAddressCard} className="section-icon" />
              <h4>Contact Information</h4>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faUser} className="label-icon" />
                  Contact Name *
                </label>
                <input
                  type="text"
                  name="contactName"
                  className="form-input"
                  value={formData.contactName}
                  onChange={onInputChange}
                  placeholder="Contact name"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faPhone} className="label-icon" />
                  Phone *
                </label>
                <input
                  type="text"
                  name="phoneNumber"
                  className="form-input"
                  value={formData.phoneNumber}
                  onChange={onInputChange}
                  placeholder="Phone number"
                  required
                />
              </div>
              <div className="form-group full-width">
                <label className="form-label">
                  <FontAwesomeIcon icon={faEnvelope} className="label-icon" />
                  Email *
                </label>
                <input
                  type="email"
                  name="emailId"
                  className="form-input"
                  value={formData.emailId}
                  onChange={onInputChange}
                  placeholder="email@company.com"
                  required
                  disabled={editMode}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <FontAwesomeIcon icon={faBell} className="section-icon" />
              <h4>Features</h4>
            </div>
            <div className="form-grid">
              <div className="form-group" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="checkbox"
                  name="emailNotificationEnabled"
                  id="emailNotificationEnabled"
                  checked={!!formData.emailNotificationEnabled}
                  onChange={onInputChange}
                  style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#2563eb" }}
                />
                <label htmlFor="emailNotificationEnabled" className="form-label" style={{ margin: 0, cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faEnvelope} className="label-icon" />
                  Enable Email Notifications
                </label>
              </div>
              <div className="form-group" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="checkbox"
                  name="whatsappNotificationEnabled"
                  id="whatsappNotificationEnabled"
                  checked={!!formData.whatsappNotificationEnabled}
                  onChange={onInputChange}
                  style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#25d366" }}
                />
                <label htmlFor="whatsappNotificationEnabled" className="form-label" style={{ margin: 0, cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faPhone} className="label-icon" />
                  Enable WhatsApp Notifications
                </label>
              </div>
              <div className="form-group" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="checkbox"
                  name="passcodeFeature"
                  id="passcodeFeature"
                  checked={!!formData.passcodeFeature}
                  onChange={onInputChange}
                  style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#2563eb" }}
                />
                <label htmlFor="passcodeFeature" className="form-label" style={{ margin: 0, cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faKey} className="label-icon" />
                  Enable Passcode Feature
                </label>
              </div>
            </div>
          </div>

          <button type="submit" className="btn-submit">
            {editMode ? "Update Client" : "Create Client"}
          </button>
        </form>
      </div>
    </div>
  );
}
